import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import FaceProcessor from '../../salon/components/FaceProcessor.jsx';
import FaceIdentityScanOverlay from '../components/FaceIdentityScanOverlay';
import FaceIdentityBrandLogo from '../components/FaceIdentityBrandLogo';
import FaceIdentityFlowNav from '../components/FaceIdentityFlowNav';
import {
  isPassportFlow,
  isPassportCheckinBoardFlow,
  isPassportGuestPath,
  faceIdentityRoute,
} from '../utils/faceIdentityRoutes';
import {
  isPresentationNavContext,
  resolvePresentationAwareHomePath,
} from '../../presentation/presentationSession';
import PassportScanConsentBar from '../components/PassportScanConsentBar';
import PassportIdleVideo from '../components/PassportIdleVideo';
import { useFaceIdentityOrgLogo } from '../hooks/useFaceIdentityOrgLogo';
import { usePassportKioskScanGate } from '../hooks/usePassportKioskScanGate';
import { usePassportProductPreview } from '../hooks/usePassportProductPreview';
import RecommendedProductsPreviewModal from '../../admin-shared/RecommendedProductsPreviewModal';
import { saveCheckinProductCart } from '../../salon-admin/utils/checkinQueueApi';
import {
  disablePassportKioskScanAfterCapture,
  clearPassportIdleVideoDelay,
  consumePassportIdleVideoDelayMs,
} from '../utils/passportKioskApi';
import { useVoiceGuide } from '../../salon/hooks/useVoiceGuide';
import { calculateFaceGuidance, calculateGuideBounds } from '../../salon/utils/faceGuidanceLogic';
import { getApiBaseUrl, SALON_API_PREFIX } from '../../salon/utils/apiConfig';
import { fetchWithTimeout } from '../../salon/utils/fetchWithTimeout';
import {
  persistFaceCapture,
  persistFacePending,
  persistFaceProfileSnapshot,
  recognizeSalonFace,
  submitPassportPendingRegistration,
  submitPassportReturningCheckin,
  warmSalonFaceEngine,
} from '../utils/faceIdentityApi';
import { captureIdentityFaceBlob } from '../utils/captureIdentityFace';
import { acquireKioskDeviceLocation, prefetchDeviceLocation } from '../../shared/deviceLocationCache';
import {
  isFranchisePassportCheckinEnabled,
  validateFranchiseFlowAuth,
} from '../../shared/franchiseFlowAuth';
import CameraPrepPlaceholder from '../../shared/CameraPrepPlaceholder';
import {
  openBuiltinFaceCameraStream,
  pickBuiltinFrontCamera,
  listMicroscopeVideoInputs,
} from '../../shared/usbMicroscopeCamera';
import { stopNativeScalpInlinePreview } from '../../shared/nyafazNativeBridge';
import { prefetchFaceMeshModule } from '../../../shared/prefetchFaceMeshModule';
import '../../shared/camera-capture-shell.css';
import '../styles/FaceIdentityLuxTheme.css';
import '../styles/FaceIdentityPageBackground.css';
import '../styles/FaceIdentityActionButtons.css';
import '../styles/FaceIdentityDetectionLayout.css';
import './FaceIdentityPage.css';

const AUTO_CAPTURE_DELAY_MS = 800;
const GRACE_PERIOD_MS = 650;
const PASSPORT_AUTO_CAPTURE_DELAY_MS = 50;
const PASSPORT_GRACE_PERIOD_MS = 520;
const PASSPORT_STABLE_FACE_FRAMES = 1;
const LIGHTING_CHECK_THROTTLE_MS = 1500;
const RECOGNIZE_MAX_SIDE = 720;
const RECOGNIZE_JPEG_QUALITY = 0.8;

const FaceIdentityPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const isPassport = isPassportFlow(location.pathname);
  const isDeskCheckin = isPassportCheckinBoardFlow(location.pathname);
  const presentationFromHub = isPresentationNavContext(location.state);
  const presentationPassportScan = isPassport && presentationFromHub;
  const {
    scanEnabled: deskPassportScanEnabled,
    idleVideoUrl,
    idleVideoKey,
    ready: deskPassportScanGateReady,
    refresh: refreshPassportKioskGate,
  } = usePassportKioskScanGate(isPassport && !presentationPassportScan);
  const passportScanEnabled = presentationPassportScan || deskPassportScanEnabled;
  const passportScanGateReady = presentationPassportScan || deskPassportScanGateReady;
  const { preview: passportProductPreview, applyCart: applyPassportProductCart } =
    usePassportProductPreview(Boolean(isPassport && passportScanGateReady && !passportScanEnabled));
  const [showIdleVideo, setShowIdleVideo] = useState(false);
  const scanHomePath = resolvePresentationAwareHomePath(location.pathname, location.state);
  const { orgLogoUrl, showLogo, onLogoError } = useFaceIdentityOrgLogo();
  const deviceInfo = location.state?.deviceInfo || null;
  const token = location.state?.token || null;
  const nextPath = location.state?.next || null;
  const mode = location.state?.mode || (nextPath ? 'salon' : 'frontdesk');

  const videoRef = useRef(null);
  const [videoEl, setVideoEl] = useState(null);
  const setVideoNode = useCallback((node) => {
    videoRef.current = node;
    setVideoEl(node);
  }, []);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const faceDetectedRef = useRef(false);
  const lightingStatusRef = useRef('checking');
  const lightingMessageRef = useRef('');
  const lastBackendLightingCheckRef = useRef(0);
  const perfectAlignmentStartTimeRef = useRef(null);
  const perfectAlignmentTimerRef = useRef(null);
  const countdownIntervalRef = useRef(null);
  const gracePeriodTimerRef = useRef(null);
  const hasAutoCapturedRef = useRef(false);
  const handleCaptureRef = useRef(null);
  const handingOffRef = useRef(false);
  const speechUnlockedRef = useRef(false);
  const latestLandmarksRef = useRef(null);
  const passportStableFramesRef = useRef(0);

  const [previewEnabled, setPreviewEnabled] = useState(false);
  const [cameraError, setCameraError] = useState('');
  const [engineReady, setEngineReady] = useState(false);
  const [engineError, setEngineError] = useState('');
  const [isCapturing, setIsCapturing] = useState(false);
  const [isIdentifying, setIsIdentifying] = useState(false);
  const [countdown, setCountdown] = useState(null);
  const [faceGuidance, setFaceGuidance] = useState({
    isInsideFrame: false,
    statusColor: 'red',
    message: 'Position your face',
    isFaceStraight: false,
    isFaceNear: false,
  });
  const [lightingStatus, setLightingStatus] = useState('checking');
  const [lightingMessage, setLightingMessage] = useState('');
  const [videoSize, setVideoSize] = useState({ width: 640, height: 480 });
  const [voiceGuideEnabled, setVoiceGuideEnabled] = useState(true);
  const [showVoiceHint, setShowVoiceHint] = useState(false);
  const [passportConsent, setPassportConsent] = useState(true);
  const passportConsentRef = useRef(true);

  const passportFastScan =
    isPassport ||
    isPassportGuestPath(location.pathname) ||
    presentationFromHub;
  const lightingPass =
    passportFastScan || lightingStatus === 'good' || lightingStatus === 'moderate';
  const consentAllowsCapture =
    !isPassport || (passportConsent && passportScanEnabled && passportScanGateReady);

  useEffect(() => {
    passportConsentRef.current = passportConsent;
  }, [passportConsent]);

  useEffect(() => {
    if (!isPassport || isFranchisePassportCheckinEnabled()) return;
    navigate(faceIdentityRoute('mobile', location.pathname), {
      replace: true,
      state: location.state,
    });
  }, [isPassport, location.pathname, location.state, navigate]);

  useEffect(() => {
    if (!passportFastScan) return undefined;
    prefetchFaceMeshModule();
    return undefined;
  }, [passportFastScan]);
  const isIOS = typeof navigator !== 'undefined' && /iPad|iPhone|iPod/.test(navigator.userAgent);

  const { unlockForIOS } = useVoiceGuide(
    'front',
    faceGuidance.statusColor === 'green',
    isCapturing || isIdentifying,
    voiceGuideEnabled && previewEnabled && !isIdentifying,
    lightingPass,
    lightingMessage || '',
    faceGuidance.message || '',
    lightingStatus !== 'checking',
  );

  const clearAlignmentTimers = useCallback(() => {
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
      countdownIntervalRef.current = null;
    }
    if (perfectAlignmentTimerRef.current) {
      clearTimeout(perfectAlignmentTimerRef.current);
      perfectAlignmentTimerRef.current = null;
    }
    if (gracePeriodTimerRef.current) {
      clearTimeout(gracePeriodTimerRef.current);
      gracePeriodTimerRef.current = null;
    }
    perfectAlignmentStartTimeRef.current = null;
    setCountdown(null);
  }, []);

  const stopCamera = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
    setPreviewEnabled(false);
  }, []);

  const checkLighting = useCallback((canvasEl) => {
    if (!canvasEl) return;
    const baseUrl = getApiBaseUrl();
    if (!baseUrl) {
      setLightingStatus('good');
      setLightingMessage('');
      lightingStatusRef.current = 'good';
      lightingMessageRef.current = '';
      return;
    }
    if (Date.now() - lastBackendLightingCheckRef.current <= LIGHTING_CHECK_THROTTLE_MS) {
      return;
    }
    lastBackendLightingCheckRef.current = Date.now();
    const frameDataUrl = canvasEl.toDataURL('image/jpeg', 0.8);
    fetchWithTimeout(`${baseUrl.replace(/\/$/, '')}${SALON_API_PREFIX}/check-lighting`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ image: frameDataUrl }),
      timeoutMs: 8000,
    })
      .then(async (res) => {
        const d = await res.json().catch(() => ({}));
        if (!res.ok) {
          setLightingStatus('good');
          setLightingMessage('');
          lightingStatusRef.current = 'good';
          lightingMessageRef.current = '';
          return;
        }
        const statusMap = { good: 'good', moderate: 'moderate', bad: 'poor' };
        const normalizedStatus = String(d?.status || '').toLowerCase();
        const msg = String(d?.message || '');
        const analysisFailed = /error analyzing lighting/i.test(msg);
        const backendStatus =
          analysisFailed ||
          d?.lighting_ok === true ||
          normalizedStatus === 'good' ||
          normalizedStatus === 'moderate'
            ? 'good'
            : statusMap[normalizedStatus] || 'poor';
        setLightingStatus(backendStatus);
        setLightingMessage(analysisFailed ? '' : msg);
        lightingStatusRef.current = backendStatus;
        lightingMessageRef.current = analysisFailed ? '' : msg;
      })
      .catch(() => {
        setLightingStatus('good');
        setLightingMessage('');
        lightingStatusRef.current = 'good';
        lightingMessageRef.current = '';
      });
  }, []);

  const startCamera = useCallback(async () => {
    try {
      setCameraError('');
      try {
        stopNativeScalpInlinePreview();
      } catch {
        /* ignore */
      }
      try {
        streamRef.current?.getTracks?.().forEach((t) => t.stop());
      } catch {
        /* ignore */
      }
      streamRef.current = null;
      if (videoRef.current) videoRef.current.srcObject = null;
      await new Promise((r) => setTimeout(r, passportFastScan ? 0 : isPassport ? 120 : 400));

      let faceCamId = '';
      if (!passportFastScan) {
        try {
          const cams = await listMicroscopeVideoInputs();
          faceCamId = pickBuiltinFrontCamera(cams)?.deviceId || '';
        } catch {
          /* facingMode fallback inside openBuiltinFaceCameraStream */
        }
      }
      const stream = await openBuiltinFaceCameraStream({
        deviceId: faceCamId,
        timeoutMs: 15000,
        width: passportFastScan ? 960 : 1280,
        height: passportFastScan ? 720 : 720,
      });
      streamRef.current = stream;
      const videoEl = videoRef.current;
      if (videoEl) {
        videoEl.srcObject = stream;
        await videoEl.play().catch(() => {});
        const applySize = () => {
          if (videoEl.videoWidth && videoEl.videoHeight) {
            setVideoSize({ width: videoEl.videoWidth, height: videoEl.videoHeight });
          }
        };
        if (videoEl.readyState >= 1) applySize();
        else videoEl.onloadedmetadata = applySize;
      }
      setPreviewEnabled(true);
      // Fail-open lighting until first check returns
      setLightingStatus('good');
      lightingStatusRef.current = 'good';
    } catch (err) {
      setCameraError(String(err?.message || err || 'Camera failed'));
      setPreviewEnabled(false);
    }
  }, [isPassport, passportFastScan]);

  useEffect(() => {
    let cancelled = false;
    setEngineError('');
    setEngineReady(false);
    void prefetchDeviceLocation();
    if (isPassport) {
      void acquireKioskDeviceLocation({ required: false });
      void validateFranchiseFlowAuth();
    }
    void warmSalonFaceEngine()
      .then((data) => {
        if (cancelled) return;
        if (data?.ready) setEngineReady(true);
        else setEngineError(data?.message || 'Face engine not ready');
      })
      .catch((err) => {
        if (cancelled) return;
        setEngineError(String(err?.message || err || 'Face engine failed to start'));
      });
    return () => {
      cancelled = true;
      clearAlignmentTimers();
      stopCamera();
      if (typeof window !== 'undefined' && window.speechSynthesis) {
        window.speechSynthesis.cancel();
      }
    };
  }, [stopCamera, clearAlignmentTimers]);

  useEffect(() => {
    if (isPassport && !passportScanGateReady) {
      clearAlignmentTimers();
      hasAutoCapturedRef.current = false;
      handingOffRef.current = false;
      stopCamera();
      return undefined;
    }
    if (!passportScanEnabled) {
      clearAlignmentTimers();
      hasAutoCapturedRef.current = false;
      stopCamera();
      return undefined;
    }
    clearPassportIdleVideoDelay();
    setShowIdleVideo(false);
    void startCamera();
    return () => {
      stopCamera();
    };
  }, [
    isPassport,
    passportScanEnabled,
    passportScanGateReady,
    startCamera,
    stopCamera,
    clearAlignmentTimers,
  ]);

  useEffect(() => {
    if (!isPassport || !passportScanGateReady || passportScanEnabled) {
      setShowIdleVideo(false);
      return undefined;
    }
    const delayMs = consumePassportIdleVideoDelayMs();
    if (delayMs <= 0) {
      setShowIdleVideo(true);
      return undefined;
    }
    setShowIdleVideo(false);
    const timer = window.setTimeout(() => {
      clearPassportIdleVideoDelay();
      setShowIdleVideo(true);
    }, delayMs);
    return () => window.clearTimeout(timer);
  }, [isPassport, passportScanEnabled, passportScanGateReady, location.key]);

  const retryCameraAndEngine = useCallback(() => {
    setCameraError('');
    setEngineError('');
    setEngineReady(false);
    handingOffRef.current = false;
    hasAutoCapturedRef.current = false;
    setIsIdentifying(false);
    clearAlignmentTimers();
    stopCamera();
    void warmSalonFaceEngine()
      .then((data) => {
        if (data?.ready) setEngineReady(true);
        else setEngineError(data?.message || 'Face engine not ready');
      })
      .catch((err) => {
        setEngineError(String(err?.message || err || 'Face engine failed to start'));
      });
    void startCamera();
  }, [clearAlignmentTimers, startCamera, stopCamera]);

  useEffect(() => {
    if (voiceGuideEnabled && isIOS && previewEnabled && !speechUnlockedRef.current) {
      setShowVoiceHint(true);
    } else {
      setShowVoiceHint(false);
    }
  }, [voiceGuideEnabled, isIOS, previewEnabled]);

  const handleUnlockSpeech = useCallback(() => {
    if (voiceGuideEnabled && !speechUnlockedRef.current && unlockForIOS) {
      speechUnlockedRef.current = true;
      setShowVoiceHint(false);
      unlockForIOS();
    }
  }, [voiceGuideEnabled, unlockForIOS]);

  const toggleVoiceGuide = useCallback(() => {
    setVoiceGuideEnabled((prev) => {
      const next = !prev;
      if (!next && typeof window !== 'undefined' && window.speechSynthesis) {
        window.speechSynthesis.cancel();
      }
      if (next && isIOS && unlockForIOS) {
        speechUnlockedRef.current = true;
        setShowVoiceHint(false);
        unlockForIOS();
      }
      return next;
    });
  }, [isIOS, unlockForIOS]);

  const handoffAfterCapture = useCallback(
    async (blob) => {
      if (handingOffRef.current) return;
      handingOffRef.current = true;
      setIsIdentifying(true);
      clearAlignmentTimers();
      try {
        const data = await recognizeSalonFace(blob);
        const captureUrl = await persistFaceCapture(blob);
        const status = String(data?.status || '').toLowerCase();
        const passportPath = isDeskCheckin;

        // Passport / Guest QR: look-familiar (soft / confirm) → mobile only — not new-user register.
        if (
          passportPath &&
          (status === 'near_match' || status === 'confirm_match')
        ) {
          if (isPassport) await disablePassportKioskScanAfterCapture();
          const pending = {
            mode: 'mobile_lookup',
            captureUrl,
            fromFaceFamiliar: true,
            confidence: data?.confidence ?? null,
            confirmCandidates: Array.isArray(data?.candidates) ? data.candidates : undefined,
            registerMessage:
              data?.message ||
              'Your face looks familiar — enter your mobile number to find your profile.',
          };
          persistFacePending(pending);
          stopCamera();
          navigate(faceIdentityRoute('mobile', location.pathname), {
            state: {
              ...pending,
              token,
              deviceInfo,
              next: nextPath,
              mode,
            },
            replace: true,
          });
          return;
        }

        // Soft failures: stay on camera and ask guest to retry.
        const retryStatuses = new Set([
          'near_match',
          'ambiguous',
          'multiple_faces',
          'low_quality',
          'face_too_small',
          'no_face',
          'blurry_image',
          'under_exposed',
          'over_exposed',
        ]);
        if (retryStatuses.has(status)) {
          handingOffRef.current = false;
          hasAutoCapturedRef.current = false;
          setIsIdentifying(false);
          setFaceGuidance((prev) => ({
            ...prev,
            statusColor: status === 'near_match' || status === 'ambiguous' ? 'orange' : 'red',
            message:
              data?.message ||
              (status === 'near_match'
                ? 'Face looks familiar — hold still and scan again'
                : status === 'ambiguous'
                  ? 'Look straight, one person only — try again'
                  : status === 'multiple_faces'
                    ? 'Only one person in frame — try again'
                    : 'Adjust position and try again'),
          }));
          return;
        }

        if (
          status === 'confirm_match' &&
          Array.isArray(data?.candidates) &&
          data.candidates.length > 0
        ) {
          stopCamera();
          navigate(faceIdentityRoute('welcome', location.pathname), {
            state: {
              confirmCandidates: data.candidates,
              confirmMessage: data.message,
              captureUrl,
              token,
              deviceInfo,
              next: nextPath,
              mode,
            },
            replace: true,
          });
          return;
        }

        if (status === 'found' && data?.user) {
          const profile = data.profile || data.user.profile || null;
          const snapshot = {
            faceUserId: data.user.id,
            user: data.user,
            profile,
            confidence: data.confidence ?? null,
            mode: 'returning',
            captureUrl,
          };
          persistFaceProfileSnapshot(snapshot);
          persistFacePending(snapshot);
          stopCamera();
          let passportCheckinId = null;
          let passportDuplicateVisit = false;
          let passportQueuedCheckin = null;
          if (passportPath) {
            if (isPassport) await disablePassportKioskScanAfterCapture();
            try {
              const queued = await submitPassportReturningCheckin({
                user: data.user,
                profile,
                captureUrl,
                isNewMember: false,
              });
              passportQueuedCheckin = queued?.checkin || null;
              passportCheckinId = passportQueuedCheckin?.id ?? null;
              passportDuplicateVisit = Boolean(queued?.reused);
            } catch (queueErr) {
              handingOffRef.current = false;
              hasAutoCapturedRef.current = false;
              setIsIdentifying(false);
              setFaceGuidance((prev) => ({
                ...prev,
                statusColor: 'red',
                message: queueErr?.message || 'Could not notify front desk — ask reception',
              }));
              return;
            }
          }
          navigate(faceIdentityRoute('welcome', location.pathname), {
            state: {
              ...snapshot,
              token,
              deviceInfo,
              next: nextPath,
              mode,
              passportGuestShown: passportPath ? true : undefined,
              checkinId: passportCheckinId || undefined,
              passportDuplicateVisit: passportPath ? passportDuplicateVisit : undefined,
              checkin:
                passportPath && passportDuplicateVisit && passportQueuedCheckin
                  ? passportQueuedCheckin
                  : undefined,
            },
            replace: true,
          });
          return;
        }

        const unknown =
          status === 'unknown' ||
          status === 'not_found' ||
          status === 'no_match' ||
          !data?.user;
        if (unknown) {
          // Passport: unrecognized → queue new-guest registration for front desk (no mobile prompt).
          if (passportPath) {
            if (isPassport) await disablePassportKioskScanAfterCapture();
            stopCamera();
            try {
              const queued = await submitPassportPendingRegistration({ captureUrl });
              const pending = {
                mode: 'pending_registration',
                captureUrl,
                referenceToken: queued.referenceToken,
                checkinId: queued.checkin?.id || null,
                fromFaceUnknown: true,
                registerReason: 'unknown',
                registerMessage:
                  data?.message ||
                  'Please wait at the front desk while we complete your registration.',
              };
              persistFacePending(pending);
              navigate(faceIdentityRoute('pending-registration', location.pathname), {
                state: {
                  ...pending,
                  token,
                  deviceInfo,
                  next: nextPath,
                  mode,
                  passportGuestShown: true,
                },
                replace: true,
              });
            } catch (queueErr) {
              handingOffRef.current = false;
              hasAutoCapturedRef.current = false;
              setIsIdentifying(false);
              setFaceGuidance((prev) => ({
                ...prev,
                statusColor: 'red',
                message:
                  queueErr?.message || 'Could not notify front desk — ask reception',
              }));
            }
            return;
          }

          const pending = {
            mode: 'mobile_lookup',
            captureUrl,
            fromFaceUnknown: true,
            registerReason: 'unknown',
            registerMessage: "We didn't recognize you — enter your mobile number to find your profile.",
          };
          persistFacePending(pending);
          stopCamera();
          navigate(faceIdentityRoute('mobile', location.pathname), {
            state: {
              ...pending,
              token,
              deviceInfo,
              next: nextPath,
              mode,
            },
            replace: true,
          });
          return;
        }

        const pending = {
          mode: 'register',
          predicted: data?.predicted || null,
          captureUrl,
          registerReason: 'enroll',
          registerMessage: data?.message || 'Create your profile to continue.',
        };
        persistFacePending(pending);
        stopCamera();
        navigate(faceIdentityRoute('register', location.pathname), {
          state: { ...pending, token, deviceInfo, next: nextPath, mode },
          replace: true,
        });
      } catch (err) {
        handingOffRef.current = false;
        hasAutoCapturedRef.current = false;
        setIsIdentifying(false);
        const raw = String(err?.message || '');
        const timedOut = /timed out/i.test(raw);
        setFaceGuidance((prev) => ({
          ...prev,
          statusColor: 'red',
          message: timedOut
            ? 'Identification timed out — hold still and try again'
            : raw || 'Identification failed — try again',
        }));
      }
    },
    [clearAlignmentTimers, deviceInfo, isDeskCheckin, isPassport, location.pathname, mode, navigate, nextPath, stopCamera, token],
  );

  const handleCapture = useCallback(() => {
    if (!previewEnabled || !videoRef.current || !canvasRef.current || isCapturing || handingOffRef.current) {
      return;
    }
    if (isPassport && !passportConsentRef.current) {
      hasAutoCapturedRef.current = false;
      return;
    }
    if (
      !passportFastScan &&
      lightingStatusRef.current !== 'good' &&
      lightingStatusRef.current !== 'moderate'
    ) {
      hasAutoCapturedRef.current = false;
      return;
    }

    hasAutoCapturedRef.current = true;
    setIsCapturing(true);
    const videoEl = videoRef.current;
    const canvasEl = canvasRef.current;

    void (async () => {
      try {
        const blob = await captureIdentityFaceBlob(canvasEl, videoEl, {
          landmarks: latestLandmarksRef.current,
          maxSide: RECOGNIZE_MAX_SIDE,
          quality: RECOGNIZE_JPEG_QUALITY,
          blurPx: 22,
        });
        if (!passportFastScan) {
          checkLighting(canvasEl);
        }
        setIsCapturing(false);
        if (!blob) {
          hasAutoCapturedRef.current = false;
          return;
        }
        void handoffAfterCapture(blob);
      } catch (_) {
        setIsCapturing(false);
        hasAutoCapturedRef.current = false;
      }
    })();
  }, [checkLighting, handoffAfterCapture, isCapturing, isPassport, passportFastScan, previewEnabled]);

  useEffect(() => {
    handleCaptureRef.current = handleCapture;
  }, [handleCapture]);

  const handleLandmarksDetected = useCallback(
    (landmarks, videoDimensions) => {
      if (handingOffRef.current || isIdentifying) return;

      if (videoDimensions?.multipleFaces) {
        latestLandmarksRef.current = null;
        faceDetectedRef.current = false;
        setFaceGuidance({
          isInsideFrame: false,
          statusColor: 'red',
          message: 'Only one person in frame',
          isFaceStraight: false,
          isFaceNear: false,
        });
        if (perfectAlignmentStartTimeRef.current) {
          clearAlignmentTimers();
        }
        return;
      }

      const faceDetected = Boolean(landmarks && landmarks.length > 0);
      faceDetectedRef.current = faceDetected;
      latestLandmarksRef.current = faceDetected ? landmarks : null;

      if (landmarks && videoDimensions) {
        if (videoDimensions.width && videoDimensions.height) {
          setVideoSize({ width: videoDimensions.width, height: videoDimensions.height });
        }

        const canvasEl = canvasRef.current;
        const videoEl = videoRef.current;
        if (!passportFastScan && canvasEl && videoEl?.videoWidth) {
          canvasEl.width = videoEl.videoWidth;
          canvasEl.height = videoEl.videoHeight;
          const ctx = canvasEl.getContext('2d');
          if (ctx) {
            ctx.drawImage(videoEl, 0, 0);
            checkLighting(canvasEl);
          }
        }

        const ovalBounds = calculateGuideBounds(videoDimensions.width, videoDimensions.height);
        const lightingOk =
          passportFastScan ||
          lightingStatusRef.current === 'good' ||
          lightingStatusRef.current === 'moderate';
        const lightingMsg = lightingMessageRef.current || '';
        const guidance = calculateFaceGuidance(
          landmarks,
          videoDimensions.width,
          videoDimensions.height,
          ovalBounds,
          lightingOk,
          lightingMsg,
        );
        setFaceGuidance(guidance);

        if (passportFastScan && faceDetected) {
          passportStableFramesRef.current += 1;
        } else if (passportFastScan) {
          passportStableFramesRef.current = 0;
        }

        const engineOkForAutoCapture = (engineReady || passportFastScan) && !engineError;
        const qualifiesForCapture =
          faceDetected &&
          lightingOk &&
          engineOkForAutoCapture &&
          !isCapturing &&
          !hasAutoCapturedRef.current &&
          previewEnabled &&
          (!isPassport || (passportConsentRef.current && passportScanEnabled)) &&
          (passportFastScan
            ? passportStableFramesRef.current >= PASSPORT_STABLE_FACE_FRAMES
            : guidance.isFaceNear &&
              (guidance.statusColor === 'green' || guidance.isInsideFrame) &&
              guidance.isFaceStraight);

        const captureDelayMs = passportFastScan ? PASSPORT_AUTO_CAPTURE_DELAY_MS : AUTO_CAPTURE_DELAY_MS;
        const graceMs = passportFastScan ? PASSPORT_GRACE_PERIOD_MS : GRACE_PERIOD_MS;

        if (qualifiesForCapture) {
          if (gracePeriodTimerRef.current) {
            clearTimeout(gracePeriodTimerRef.current);
            gracePeriodTimerRef.current = null;
          }

          if (!perfectAlignmentStartTimeRef.current) {
            perfectAlignmentStartTimeRef.current = Date.now();
            setCountdown(Math.max(1, Math.ceil(captureDelayMs / 1000)));

            if (perfectAlignmentTimerRef.current) {
              clearTimeout(perfectAlignmentTimerRef.current);
            }
            if (countdownIntervalRef.current) {
              clearInterval(countdownIntervalRef.current);
              countdownIntervalRef.current = null;
            }

            countdownIntervalRef.current = setInterval(() => {
              if (!perfectAlignmentStartTimeRef.current) return;
              const elapsed = Date.now() - perfectAlignmentStartTimeRef.current;
              const remaining = Math.ceil((captureDelayMs - elapsed) / 1000);
              if (remaining > 0) setCountdown(remaining);
              else {
                clearInterval(countdownIntervalRef.current);
                countdownIntervalRef.current = null;
                setCountdown(0);
              }
            }, 50);

            perfectAlignmentTimerRef.current = setTimeout(() => {
              if (countdownIntervalRef.current) {
                clearInterval(countdownIntervalRef.current);
                countdownIntervalRef.current = null;
              }
              perfectAlignmentStartTimeRef.current = null;
              perfectAlignmentTimerRef.current = null;
              setCountdown(null);
              passportStableFramesRef.current = 0;
              if (handleCaptureRef.current) handleCaptureRef.current();
            }, captureDelayMs);
          }
        } else {
          // Soft grace for brief wobble / yellow flicker — don't instantly cancel
          if (!passportFastScan) {
            setCountdown(null);
          }
          if (countdownIntervalRef.current) {
            clearInterval(countdownIntervalRef.current);
            countdownIntervalRef.current = null;
          }
          if (perfectAlignmentStartTimeRef.current && !gracePeriodTimerRef.current) {
            gracePeriodTimerRef.current = setTimeout(() => {
              perfectAlignmentStartTimeRef.current = null;
              gracePeriodTimerRef.current = null;
              if (perfectAlignmentTimerRef.current) {
                clearTimeout(perfectAlignmentTimerRef.current);
                perfectAlignmentTimerRef.current = null;
              }
            }, graceMs);
          }
        }
      } else {
        passportStableFramesRef.current = 0;
        latestLandmarksRef.current = null;
        setFaceGuidance({
          isInsideFrame: false,
          statusColor: 'red',
          message: 'Position your face',
          isFaceStraight: false,
          isFaceNear: false,
        });
        if (perfectAlignmentStartTimeRef.current) {
          clearAlignmentTimers();
        }
      }
    },
    [
      checkLighting,
      clearAlignmentTimers,
      engineError,
      engineReady,
      isCapturing,
      isIdentifying,
      isPassport,
      passportFastScan,
      passportScanEnabled,
      previewEnabled,
    ],
  );

  const passportDeskIdle =
    isPassport &&
    passportScanGateReady &&
    !passportScanEnabled &&
    !passportProductPreview?.active;
  const hideCameraPreview = !previewEnabled || passportDeskIdle;

  return (
    <div
      className={`face-identity-page face-identity-page--scan face-detection-page page-bg fi-lux-theme${
        isPassport ? ' face-identity-page--passport' : ''
      }`}
    >
      <div className="face-detection-page-nav face-identity-capture-nav">
        <FaceIdentityBrandLogo
          showLogo={showLogo}
          orgLogoUrl={orgLogoUrl}
          onLogoError={onLogoError}
          className="face-identity-capture-brand"
          imgClassName="fi-brand-logo__img fi-brand-logo__img--on-camera"
          fallbackClassName="fi-brand-logo__fallback fi-brand-logo__fallback--on-camera face-identity-brand face-identity-brand--on-camera"
        />
        <div className="face-identity-capture-nav-actions">
          <button
            type="button"
            className={`face-identity-voice-toggle${voiceGuideEnabled ? ' is-on' : ''}`}
            onClick={toggleVoiceGuide}
            aria-pressed={voiceGuideEnabled}
          >
            Voice guide {voiceGuideEnabled ? 'on' : 'off'}
          </button>
          {!isPassport ? (
            <FaceIdentityFlowNav
              showBack={false}
              homePath={scanHomePath}
              onHome={() => {
                clearAlignmentTimers();
                stopCamera();
                navigate(scanHomePath, { replace: true });
              }}
            />
          ) : (
            <FaceIdentityFlowNav
              showBack={false}
              showHome={presentationFromHub}
              homePath={scanHomePath}
              showLogout={false}
              showRefresh
              onHome={() => {
                clearAlignmentTimers();
                stopCamera();
                navigate(scanHomePath, { replace: true });
              }}
              onRefresh={() => {
                clearAlignmentTimers();
                handingOffRef.current = false;
                hasAutoCapturedRef.current = false;
                setIsIdentifying(false);
                window.location.reload();
              }}
            />
          )}
        </div>
      </div>

      <main className="face-detection-page-main" onClick={handleUnlockSpeech} role="presentation">
        <video
          ref={setVideoNode}
          playsInline
          muted
          className={`video-preview${hideCameraPreview ? ' video-preview--preparing' : ''}`}
        />
        {isPassport && passportScanGateReady && !passportScanEnabled ? (
          passportProductPreview?.active ? null : showIdleVideo && idleVideoUrl ? (
            <PassportIdleVideo
              src={idleVideoUrl}
              mediaKey={idleVideoKey}
              visible
              onRecover={refreshPassportKioskGate}
            />
          ) : (
            <CameraPrepPlaceholder
              variant="dark"
              message={
                idleVideoUrl
                  ? 'Thank you. Returning shortly…'
                  : 'Welcome. One moment, please.'
              }
            />
          )
        ) : null}
        {!previewEnabled && !(isPassport && passportScanGateReady && !passportScanEnabled) ? (
          <CameraPrepPlaceholder variant="dark" message="Starting camera…" />
        ) : null}
        <canvas ref={canvasRef} className="capture-canvas" />

        {voiceGuideEnabled && isIOS && previewEnabled && showVoiceHint && (
          <button
            type="button"
            className="face-detection-voice-enable-btn face-identity-voice-enable-btn"
            onClick={handleUnlockSpeech}
            aria-label="Enable voice guide sound"
          >
            Tap to enable voice guide sound
          </button>
        )}

        {previewEnabled && !passportDeskIdle && (
          <FaceIdentityScanOverlay
            statusColor={isIdentifying ? 'green' : faceGuidance.statusColor}
            message={faceGuidance.message}
            videoWidth={videoSize.width}
            videoHeight={videoSize.height}
            showOverlay
            countdown={isIdentifying ? null : countdown}
            lightingOk={lightingPass}
            faceDetected={faceDetectedRef.current || isIdentifying}
            faceStraight={faceGuidance.isFaceStraight || isIdentifying}
            scanning={isIdentifying}
            engineReady={engineReady && !engineError}
          />
        )}

        {previewEnabled && !passportDeskIdle && !isIdentifying && videoEl && (
          <FaceProcessor
            videoElement={videoEl}
            onLandmarksDetected={handleLandmarksDetected}
            enabled={previewEnabled && !isIdentifying}
            quickStart={passportFastScan}
          />
        )}

        {(cameraError || engineError) && (
          <div className="face-identity-identifying-overlay face-identity-identifying-overlay--error">
            <p>{engineError || cameraError}</p>
            <div className="face-identity-identifying-overlay__actions">
              <button
                type="button"
                className="face-identity-identifying-overlay__btn face-identity-identifying-overlay__btn--primary"
                onClick={retryCameraAndEngine}
              >
                Try again
              </button>
              {!isPassport ? (
                <button
                  type="button"
                  className="face-identity-identifying-overlay__btn"
                  onClick={() => {
                    clearAlignmentTimers();
                    stopCamera();
                    navigate(scanHomePath, { replace: true });
                  }}
                >
                  Home
                </button>
              ) : null}
            </div>
          </div>
        )}
      </main>

      {isPassport ? (
        <PassportScanConsentBar checked={passportConsent} onCheckedChange={setPassportConsent} />
      ) : null}

      {isPassport && passportProductPreview?.active ? (
        <RecommendedProductsPreviewModal
          open
          customerFacing
          allowCart
          allowPlaceOrder={false}
          onClose={() => {}}
          customerName={passportProductPreview.customer_name || 'Guest'}
          products={passportProductPreview.recommended_products}
          treatments={passportProductPreview.recommended_treatments}
          initialCart={passportProductPreview.product_cart}
          purchases={passportProductPreview.product_purchases}
          skinSessionId={passportProductPreview.skin_session_id || passportProductPreview.salon_reference || ''}
          hairSessionId={passportProductPreview.hair_insight_session_id || ''}
          subtitle="Review recommended services and products — front desk will complete your purchase."
          onSaveCart={async (items) => {
            const checkinId = passportProductPreview.checkin_id;
            if (!checkinId) throw new Error('Visit not available');
            const data = await saveCheckinProductCart(checkinId, items, { source: 'passport' });
            applyPassportProductCart(
              Array.isArray(data?.product_cart) ? data.product_cart : items,
            );
          }}
        />
      ) : null}
    </div>
  );
};

export default FaceIdentityPage;
